/**
 * Busca de endereço por CEP (ViaCEP, público e sem chave). É o jeito de deixar
 * o local do serviço exato sem obrigar o prestador a digitar rua e bairro.
 */
export interface CepAddress {
  cep: string;
  street: string;
  neighborhood: string;
  city: string;
  state: string;
}

const CEP_DIGITS = 8;

export function cepDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, CEP_DIGITS);
}

export function isValidCep(value: string): boolean {
  return cepDigits(value).length === CEP_DIGITS;
}

export async function fetchAddressByCep(value: string): Promise<CepAddress> {
  const digits = cepDigits(value);
  if (digits.length !== CEP_DIGITS) {
    throw new Error('Informe um CEP com 8 dígitos.');
  }

  const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
  if (!response.ok) {
    throw new Error('Não foi possível buscar o CEP agora.');
  }

  const data = (await response.json()) as {
    erro?: boolean | string;
    cep?: string;
    logradouro?: string;
    bairro?: string;
    localidade?: string;
    uf?: string;
  };

  if (data.erro) {
    throw new Error('CEP não encontrado.');
  }

  return {
    cep: data.cep ?? digits,
    street: data.logradouro ?? '',
    neighborhood: data.bairro ?? '',
    city: data.localidade ?? '',
    state: data.uf ?? '',
  };
}

/** Monta o endereço numa linha só, que é como o serviço guarda o local. */
export function composeAddress({
  base,
  number,
  complement,
}: {
  base: CepAddress;
  number?: string;
  complement?: string;
}): string {
  const streetPart = [base.street, number?.trim()].filter(Boolean).join(', ');
  const withComplement = [streetPart, complement?.trim()]
    .filter(Boolean)
    .join(' - ');
  const cityPart = [base.city, base.state].filter(Boolean).join(' - ');

  return [withComplement, base.neighborhood, cityPart, `CEP ${base.cep}`]
    .filter(Boolean)
    .join(', ');
}

/**
 * O caminho de volta: a linha guardada vira campos editáveis de novo.
 *
 * Existe porque endereço salvo é texto, e todo lugar que reaproveita esse
 * texto (o atalho "usar o endereço do meu cadastro", a retomada da entrevista)
 * precisava mostrar rua, número e cidade em campos — não uma linha inteira num
 * campo só, ou pior, campos vazios com um botão que salva assim mesmo.
 *
 * É tolerante de propósito: o que não reconhecer vira rua, e ele corrige. Só
 * o que a linha realmente disser é preenchido.
 */
export interface ParsedAddress {
  cep: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

const EMPTY_PARSED: ParsedAddress = {
  cep: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
};

export function parseAddress(line: string): ParsedAddress {
  const parts = line
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return EMPTY_PARSED;

  const parsed = { ...EMPTY_PARSED };
  const rest: string[] = [];

  parts.forEach(part => {
    const cep = part.match(/^CEP\s*([\d-]{8,10})$/i);
    if (cep) {
      parsed.cep = cep[1];
      return;
    }
    // "Contagem - MG": a sigla no fim é o que identifica a parte da cidade.
    const city = part.match(/^(.+?)\s*-\s*([A-Z]{2})$/);
    if (city) {
      parsed.city = city[1].trim();
      parsed.state = city[2];
      return;
    }
    rest.push(part);
  });

  // "Rua das Flores - Sala 2": o traço separa o complemento da rua.
  const [street, complement] = (rest.shift() ?? '').split(' - ');
  parsed.street = street?.trim() ?? '';
  if (complement) parsed.complement = complement.trim();

  // O número sai como parte própria ("Rua das Flores, 120"): é o primeiro
  // pedaço que sobrou e que começa com dígito.
  const numberAt = rest.findIndex(part => /^\d/.test(part));
  if (numberAt >= 0) {
    const [value, extra] = rest[numberAt].split(' - ');
    parsed.number = value.trim();
    if (extra && !parsed.complement) parsed.complement = extra.trim();
    rest.splice(numberAt, 1);
  }

  if (rest.length > 0) parsed.neighborhood = rest[0];

  return parsed;
}
